/**
 * SQL 문장 분리와 **바인드 변수 검출** — 편집기와 실행 경로가 같은 것을 본다.
 *
 * 예전에는 App.js 와 SQLEditor.js 에 같은 구현이 **두 벌** 있었다("SQLEditor 와 동일한 로직"
 * 이라는 주석까지 붙은 채로). 한쪽만 고치면 편집기가 표시하는 바인드와 실행이 묻는 바인드가
 * 갈린다 — 그래서 여기 한 곳만 둔다.
 *
 * ## 무엇이 바인드가 아닌가
 *
 * 예전 정규식은 `/[:&]([a-zA-Z0-9_]+)/g` 한 줄이었다. 문맥을 전혀 보지 않아서
 * `2026. 9. 4. 오후 10:58:47` 에서 **`:58`·`:47` 을 바인드로 잡았다**(실제 신고).
 * 아래를 모두 바인드가 아닌 것으로 본다:
 *
 * | 모양 | 왜 |
 * |---|---|
 * | `10:58:47` | 콜론 앞이 **숫자·글자**면 이름의 일부이지 바인드 시작이 아니다 (시각·`a:b`) |
 * | `'... :x ...'` | 문자열 리터럴 안. 값이지 바인드가 아니다 |
 * | `-- :x` · `/* :x *​/` | 주석 안 |
 * | `x::int` | PostgreSQL 캐스트 |
 * | `:1` 앞이 숫자 | 위와 같은 이유 |
 *
 * 반대로 **남기는 것**: `:name`(이름), `:1`(오라클 위치 바인드), `&name`·`&&name`(치환 변수).
 *
 * ## 문자열 규칙
 *
 * SQL 에서 작은따옴표 안의 따옴표는 **두 번 써서**(`''`) 표현한다. 역슬래시 이스케이프는 없다.
 * 예전 구현은 `\` 를 이스케이프로 봤는데, 그러면 윈도우 경로 `'C:\temp\'` 의 닫는 따옴표를
 * 먹어서 그 뒤 전체가 문자열 안으로 취급된다 — DB 도구에서 실제로 만날 입력이다.
 */

/** 이름 바인드의 첫 글자·이어지는 글자. 오라클은 `$`·`#` 도 식별자에 쓴다. */
const NAME_START = /[A-Za-z]/;
const NAME_PART = /[A-Za-z0-9_$#]/;
/**
 * 콜론 **앞**에 이것이 있으면 바인드가 아니다 — 이름의 일부이거나 시각이다.
 * 콜론도 포함한다: `x::int` 는 앞 콜론이 `x` 때문에 걸러지므로, 뒤 콜론은 **앞 콜론**을 보고 걸러야 한다.
 */
const NOT_BEFORE = /[A-Za-z0-9_$#:]/;

/**
 * 문장 하나를 훑으며 바인드 이름을 모은다. 순서를 유지하고 중복은 한 번만 담는다.
 * @param {string} text
 * @returns {string[]}
 */
export function detectBindVariables(text) {
  if (!text) return [];
  const found = [];
  const add = (n) => { if (n && !found.includes(n)) found.push(n); };

  let i = 0;
  const n = text.length;
  while (i < n) {
    const ch = text[i];

    // ── 문자열 리터럴: 닫는 따옴표까지 통째로 건너뛴다 ('' 는 리터럴 안의 따옴표)
    if (ch === "'") {
      i++;
      while (i < n) {
        if (text[i] === "'") {
          if (text[i + 1] === "'") { i += 2; continue; }   // '' → 리터럴 한 글자
          i++; break;
        }
        i++;
      }
      continue;
    }
    // ── 따옴표 식별자 "..."
    if (ch === '"') {
      i++;
      while (i < n && text[i] !== '"') i++;
      i++;
      continue;
    }
    // ── 줄 주석
    if (ch === '-' && text[i + 1] === '-') {
      while (i < n && text[i] !== '\n') i++;
      continue;
    }
    // ── 블록 주석
    if (ch === '/' && text[i + 1] === '*') {
      i += 2;
      while (i < n && !(text[i] === '*' && text[i + 1] === '/')) i++;
      i += 2;
      continue;
    }

    if (ch === ':' || ch === '&') {
      // 앞이 식별자 문자면 바인드가 아니다 — `10:58`(시각), `a:b`
      const prev = i > 0 ? text[i - 1] : '';
      if (prev && NOT_BEFORE.test(prev)) { i++; continue; }

      if (ch === ':' && text[i + 1] === ':') { i += 2; continue; }   // PostgreSQL 캐스트
      let j = i + 1;
      if (ch === '&' && text[j] === '&') j++;                        // SQL*Plus &&name

      const start = j;
      if (ch === ':' && /[0-9]/.test(text[j])) {
        while (j < n && /[0-9]/.test(text[j])) j++;                  // 오라클 위치 바인드 :1
      } else if (NAME_START.test(text[j] || '')) {
        while (j < n && NAME_PART.test(text[j])) j++;
      } else {
        i++;                                                        // `:` 뒤에 이름이 없다
        continue;
      }
      add(text.slice(start, j));
      i = j;
      continue;
    }

    i++;
  }
  return found;
}

/**
 * 세미콜론으로 문장을 나눈다. 문자열·주석 안의 세미콜론은 구분자가 아니다.
 * @returns {Array<{text:string, start:number, end:number, bindVars:string[]}>}
 */
export function parseStatements(queryText) {
  if (!queryText) return [];

  const out = [];
  const n = queryText.length;
  let start = 0;
  let i = 0;

  const push = (from, to) => {
    const text = queryText.substring(from, to).trim();
    if (text) out.push({ text, start: from, end: to, bindVars: detectBindVariables(text) });
  };

  while (i < n) {
    const ch = queryText[i];
    if (ch === "'") {
      i++;
      while (i < n) {
        if (queryText[i] === "'") {
          if (queryText[i + 1] === "'") { i += 2; continue; }
          i++; break;
        }
        i++;
      }
      continue;
    }
    if (ch === '"') { i++; while (i < n && queryText[i] !== '"') i++; i++; continue; }
    if (ch === '-' && queryText[i + 1] === '-') { while (i < n && queryText[i] !== '\n') i++; continue; }
    if (ch === '/' && queryText[i + 1] === '*') {
      i += 2;
      while (i < n && !(queryText[i] === '*' && queryText[i + 1] === '/')) i++;
      i += 2;
      continue;
    }
    if (ch === ';') { push(start, i); start = i + 1; }
    i++;
  }
  push(start, n);
  return out;
}


/**
 * 입력값이 **어떤 타입으로 묶일지** 추론한다. 편집기의 바인드 창과 SQL 실행계획 분석 패널이
 * **같은 것**을 쓴다 — 한쪽은 NUMBER, 한쪽은 VARCHAR2 로 보이면 같은 값으로 계획이 갈린다
 * (숫자 컬럼에 문자열을 묶으면 암시적 형변환으로 계획이 달라진다).
 * 실제 변환은 백엔드(`flattenBindVariables`)가 한다.
 * @param {*} value
 * @returns {'NULL'|'NUMBER'|'DATE'|'VARCHAR2'}
 */
export function inferBindType(value) {
  const v = String(value ?? '').trim();
  if (v === '') return 'NULL';
  if (/^-?\d+(\.\d+)?$/.test(v)) return 'NUMBER';
  if (/^\d{4}[-/]\d{2}[-/]\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/.test(v)) return 'DATE';
  return 'VARCHAR2';
}
